import React, { useEffect, useState } from "react";

import { Form, Formik, FormikErrors } from "formik";
import toast from "react-hot-toast";
import { Navigate, useNavigate } from "react-router-dom";

import { ROUTE_DESTINATIONS } from "../../constants/routes";
import {
  DestinationLabelMap,
  DestinationTabs,
  DestinationTypeEnum,
} from "../../enums/destination";
import useSelectedAccount from "../../hooks/useSelectedAccount";
import {
  AllDestinations,
  GoogleDocsDestination,
  ShopifyDestination,
  WordPressDestination,
} from "../../models/destination";
import {
  useConnectGoogleDestinationMutation,
  useCreateGoogleDestinationMutation,
  useTestSavedDestinationMutation,
} from "../../redux/services/destinations";
import { useDestination } from "./hooks/useDestination";
import { generatePayload } from "./utils/apiPayloadUtils";
import {
  errorMessages,
  saveWithoutTesting,
  validationSchema,
} from "./utils/formValidationUtils";

import PageHeading from "../../components/common/PageHeading";
import { Text } from "../../components/common/Typography";
import SpringContainer from "../../components/containers/SpringContainer";
import Button from "../../components/form/Button";
import TemplateContainer from "../TemplateForm/components/common/TemplateContainer";
import DeleteDestinationButton from "./components/common/DeleteDestinationButton";
import DestinationSaveButton from "./components/common/DestinationSaveButton";
import GoogleDocs from "./components/forms/GoogleDocs";
import Shopify from "./components/forms/Shopify";
import WordPress from "./components/forms/WordPress";
import DestinationSaveModal from "./components/modals/DestinationSaveModal";

const Destination = () => {
  const navigate = useNavigate();

  const {
    destinationId,
    destinationType,
    destination,
    formValues,
    initialSnapshot,

    createDestination,
    isCreateLoading,
    isCreateSuccess,
    isCreateError,
    createErrorMessage,

    updateDestination,
    isUpdateLoading,
    isUpdateSuccess,
    isUpdateError,
    updateErrorMessage,

    testDestination,
    isTestLoading,
    isTestError,
    testSuccessData,
    testErrorMessage,
  } = useDestination();

  const [showTestModal, setShowTestModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Google Docs: connecting goes through Google's own consent screen (the browser leaves the app and comes back)
  const [connectGoogleDestination, { isLoading: isConnectingGoogle }] =
    useConnectGoogleDestinationMutation();
  const [createGoogleDestination, { isLoading: isCreatingGoogle }] =
    useCreateGoogleDestinationMutation();
  const [testSavedDestination, { isLoading: isTestingGoogle }] =
    useTestSavedDestinationMutation();

  const { accountId } = useSelectedAccount();

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      navigate(ROUTE_DESTINATIONS);
    } else if (testSuccessData) {
      setShowTestModal(false);
      setShowSaveModal(true);
    }
  }, [isCreateSuccess, isUpdateSuccess, testSuccessData, navigate]);

  useEffect(() => {
    const isCreateUpdateError =
      isCreateError ||
      isUpdateError ||
      createErrorMessage ||
      updateErrorMessage;

    if (isCreateUpdateError) {
      toast.error(
        ((createErrorMessage || updateErrorMessage) as any)?.data?.detail ??
          "Something went wrong."
      );
    }
  }, [isCreateError, isUpdateError, createErrorMessage, updateErrorMessage]);

  const handleSaveDestination = async (values: any) => {
    if (!accountId) return;
    const payload = generatePayload(destination, values, accountId);

    try {
      if (destinationId) {
        await updateDestination({
          ...payload,
          destinationId: destinationId,
          templateIdList: values.templateIdList ?? [],
        }).unwrap();
      } else {
        await createDestination(payload).unwrap();
      }
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  const handleTestDestination = async (values: any) => {
    if (!accountId) return;
    const payload = generatePayload(destination, values, accountId);

    try {
      await testDestination(payload).unwrap();
    } catch (error) {
      console.error("Test failed:", error);
    }
  };

  const startGoogleConnect = async (id: string) => {
    if (!accountId) return;
    const { authorizeUrl } = await connectGoogleDestination({
      accountId,
      destinationId: id,
    }).unwrap();
    window.location.assign(authorizeUrl);
  };

  // new Google Docs destination: nothing is saved here. The browser goes to Google, and the server saves the
  // destination only once the connection has fully succeeded (an error, or a cancel at Google, saves nothing).
  const handleCreateGoogleDestination = async (values: any) => {
    if (!accountId) return;
    try {
      const { authorizeUrl } = await createGoogleDestination({
        accountId,
        name: values.name,
        folderName: values.destination.folderName,
      }).unwrap();
      window.location.assign(authorizeUrl);
    } catch (error: any) {
      toast.error(
        error?.data?.detail ?? "Could not start the Google connection."
      );
    }
  };

  const handleConnectGoogle = async () => {
    if (!destinationId) return;
    try {
      await startGoogleConnect(destinationId);
    } catch (error: any) {
      toast.error(
        error?.data?.detail ?? "Could not start the Google connection."
      );
    }
  };

  const handleTestGoogle = async () => {
    if (!accountId || !destinationId) return;
    try {
      const result: any = await testSavedDestination({
        accountId,
        destinationId,
      }).unwrap();
      toast.success(result?.message ?? "Connected to Google Drive.");
    } catch (error: any) {
      toast.error(error?.data?.detail ?? "The connection test failed.");
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ADDED}`);
    }
  };

  if (!destinationType) return <Navigate to={ROUTE_DESTINATIONS} />;

  if (!formValues) return null;

  const isEdit = Boolean(destinationId);
  return (
    <SpringContainer>
      <Formik
        initialValues={formValues}
        enableReinitialize={true}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={(values: AllDestinations) => {
          handleSaveDestination(values);
        }}
        validationSchema={validationSchema[destinationType](isEdit)}
      >
        {({ values, errors, validateForm, setFieldValue }) => {
          return (
            <Form
              className="flex flex-col gap-6 flex-wrap transition-all duration-200"
              noValidate
            >
              <TemplateContainer
                className="max-w-[60.5rem] rounded"
                style={{ padding: 0 }}
              >
                <div className="py-4 px-4 sm:px-6 relative bg-gray-50 rounded-t-lg">
                  <PageHeading
                    heading={`Configure ${DestinationLabelMap[formValues.destinationTypeId]}`}
                  />
                  <Text className="text-gray-500 text-sm">
                    {destinationType === DestinationTypeEnum.GOOGLE_DOCS
                      ? "Save every generated article as a Google Doc in a Google Drive folder"
                      : "Provide your account details to establish a connection"}
                  </Text>
                  {formValues?.destinationId && (
                    <DeleteDestinationButton
                      destinationId={formValues?.destinationId}
                      destinationTypeId={formValues.destinationTypeId}
                    />
                  )}
                </div>
                <div className="bg-gray-200 h-px w-full" />
                {destinationType === DestinationTypeEnum.WORDPRESS && (
                  <WordPress
                    values={values as WordPressDestination}
                    errors={errors as FormikErrors<WordPressDestination>}
                    setFieldValue={setFieldValue}
                  />
                )}
                {destinationType === DestinationTypeEnum.SHOPIFY && (
                  <Shopify
                    values={values as ShopifyDestination}
                    errors={errors as FormikErrors<ShopifyDestination>}
                    setFieldValue={setFieldValue}
                  />
                )}
                {destinationType === DestinationTypeEnum.GOOGLE_DOCS && (
                  <GoogleDocs
                    values={values as GoogleDocsDestination}
                    errors={errors as FormikErrors<GoogleDocsDestination>}
                    setFieldValue={setFieldValue}
                    isEdit={isEdit}
                    onConnect={handleConnectGoogle}
                    isConnecting={isConnectingGoogle}
                    onTest={handleTestGoogle}
                    isTesting={isTestingGoogle}
                  />
                )}
              </TemplateContainer>

              <div className="flex items-center justify-between max-w-5xl">
                <div className="flex items-center gap-1">
                  {destinationType === DestinationTypeEnum.GOOGLE_DOCS ? (
                    // no typed credentials to test: save (and, for a new one, go on to connect the Google account)
                    <Button
                      variant="primary"
                      className="min-w-[4rem]"
                      onClick={() => {
                        validateForm().then((formErrors) => {
                          if (Object.keys(formErrors).length === 0) {
                            if (isEdit) {
                              handleSaveDestination(values);
                            } else {
                              handleCreateGoogleDestination(values);
                            }
                          }
                        });
                      }}
                      loading={
                        isUpdateLoading ||
                        isConnectingGoogle ||
                        isCreatingGoogle
                      }
                    >
                      {isEdit ? "Save" : "Connect Google and save"}
                    </Button>
                  ) : !isEdit ? (
                    <Button
                      variant="primary"
                      className="min-w-[4rem]"
                      onClick={() => {
                        validateForm().then((formErrors) => {
                          if (Object.keys(formErrors).length === 0) {
                            handleTestDestination(values);
                          }
                        });
                      }}
                      loading={
                        isCreateLoading || isUpdateLoading || isTestLoading
                      }
                    >
                      Test Connection
                    </Button>
                  ) : (
                    <DestinationSaveButton
                      showTestModal={showTestModal}
                      isLoading={isCreateLoading || isUpdateLoading}
                      isTestLoading={isTestLoading}
                      testErrorMessage={
                        isTestError
                          ? (testErrorMessage as any)?.data?.detail ??
                            errorMessages[destinationType]
                          : null
                      }
                      onTest={() => handleTestDestination(values)}
                      onTestCancel={() => setShowTestModal(false)}
                      onSave={() => {
                        validateForm().then((formErrors) => {
                          if (Object.keys(formErrors).length === 0) {
                            if (
                              initialSnapshot &&
                              saveWithoutTesting(values, initialSnapshot)
                            ) {
                              handleSaveDestination(values);
                            } else {
                              setShowTestModal(true);
                            }
                          }
                        });
                      }}
                    />
                  )}
                  <Button
                    variant="transparent"
                    onClick={handleGoBack}
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              {isTestError && (
                <div className="text-red-500 text-sm">
                  Connection test failed.
                </div>
              )}
              {testErrorMessage && (
                <div className="max-w-[61rem] text-sm text-red-500 break-all border border-red-200 rounded p-4 max-h-96 overflow-y-scroll no-scrollbar">
                  <div className="mb-1">
                    {(testErrorMessage as any)?.status}
                  </div>
                  <div>{(testErrorMessage as any)?.data?.detail}</div>
                </div>
              )}

              {/* common for create and edit */}
              {showSaveModal && (
                <DestinationSaveModal
                  message={testSuccessData?.message}
                  loading={isCreateLoading || isUpdateLoading}
                  onSave={() => handleSaveDestination(values)}
                  onRequestClose={() => setShowSaveModal(false)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </SpringContainer>
  );
};

export default Destination;
